import { z } from 'zod'
import type { GameConfigIssueCode } from '../shared/errorCodes/configIssues.ts'
import type { FeatureKind } from './features.ts'
import { isFeatureKind } from './features.ts'

/**
 * A game's configuration: its math (symbols, reel strips, paylines, pays)
 * and its rules (bets, gamble, free spins). The game server plays by it;
 * a cabinet gets only the part it shows (`PresentationConfig`). Nobody
 * writes one by hand: the engine combines a game's definition in code
 * (grid, symbols, pay groups, lines, the free games' kind, trigger symbol and cap) with the settings a
 * configuration version stores (`LineSlotSettings`, below) into it
 * (`gameConfigOf`). The engine's `validateGameConfig` checks the rules
 * between its fields before a server runs it.
 */

// -----------------------------------------------------------------------------
// Game kind
// -----------------------------------------------------------------------------

/**
 * Every kind of game a server can run: the key of the engine's game module
 * registry, and the `kind` of a play request and its reply.
 */
export const gameKindSchema = z.enum(['lineSlot'])

/** A kind of game: `'lineSlot'`, a reel slot that pays on lines. */
export type GameKind = z.infer<typeof gameKindSchema>

/**
 * Whether `value` is a kind this build knows: a client checks the `kind` of
 * a game it was offered before it runs it.
 */
export function isGameKind(value: unknown): value is GameKind {
  return gameKindSchema.safeParse(value).success
}

// -----------------------------------------------------------------------------
// Symbols
// -----------------------------------------------------------------------------

/**
 * Behavioral traits a symbol can have. A symbol with no traits is a
 * standard "regular" symbol that pays on payline matches.
 * Traits can be combined — e.g. the Book in Book of Ra is both Wild and Scatter.
 *
 * - Wild:    Substitutes for any regular symbol in a winning combination.
 * - Scatter: Pays based on total count anywhere on the grid (ignores paylines).
 * - Bonus:   Triggers a bonus/feature round. Does not pay directly.
 */
export const SymbolTrait = {
  Wild: 'wild',
  Scatter: 'scatter',
  Bonus: 'bonus',
} as const

/** One of `SymbolTrait`'s values, as a config's JSON names it (`"wild"`). */
export type SymbolTrait = (typeof SymbolTrait)[keyof typeof SymbolTrait]

/**
 * Definition of a single symbol used in the game.
 */
export interface SymbolDefinition {
  /** Unique identifier, e.g. "cherry", "wild", "scatter" */
  id: string

  /** Behavioral traits — determines how the evaluator treats this symbol. Empty = regular. */
  traits: SymbolTrait[]

  /**
   * Payout multipliers keyed by match count: the counts it names are the
   * only ones that pay. Example: { 3: 5, 4: 15, 5: 50 } means
   *   3 in a row = 5× bet, 4 in a row = 15× bet, 5 in a row = 50× bet,
   * and 2 in a row pays nothing.
   *
   * Wilds may or may not have their own payouts.
   * Scatters use this too, but keyed by total count on the grid.
   */
  payouts: Record<number, number>
}

// -----------------------------------------------------------------------------
// Reel Strips
// -----------------------------------------------------------------------------

/**
 * A virtual reel strip is an array of symbol IDs.
 * The length of the array determines the total number of stops on that reel.
 * A symbol's frequency = how many times its ID appears in the array.
 *
 * Example: ["cherry", "blank", "blank", "seven", "cherry", "blank", "bar", ...]
 * If this array has 100 entries and "cherry" appears 15 times,
 * the probability of landing on cherry is 15/100 = 15%.
 */
export type ReelStrip = string[]

/**
 * Complete set of reel strips for a game.
 * reelStrips[0] is reel 1, reelStrips[1] is reel 2, etc.
 */
export type ReelStripSet = ReelStrip[]

// -----------------------------------------------------------------------------
// Paylines
// -----------------------------------------------------------------------------

/**
 * A payline is an array of row indices, one per reel.
 * payline[reelIndex] = which row to look at on that reel.
 *
 * Example for a 5-reel game:
 *   [1, 1, 1, 1, 1] = straight middle line
 *   [0, 1, 2, 1, 0] = V-shape
 */
export type PaylineDefinition = number[]

// -----------------------------------------------------------------------------
// Game Configuration
// -----------------------------------------------------------------------------

/**
 * Complete configuration for a slot game.
 * This is the "blueprint" that defines everything about a game's math.
 */
export interface GameConfig {
  /** The kind of game: picks the game module a server plays it with. */
  kind: GameKind

  /** Number of reels */
  reelCount: number

  /** Number of visible rows per reel */
  rowCount: number

  /** All symbols used in this game */
  symbols: SymbolDefinition[]

  /** Virtual reel strips (one per reel) */
  reelStrips: ReelStripSet

  /** Payline definitions */
  paylines: PaylineDefinition[]

  /** Available bet-per-line options */
  betOptions: number[]

  /** Available counts of played lines */
  payLineOptions: number[]

  /** The gamble's rules: which wins it takes and how much it risks. */
  gamble: GambleConfig

  /** Free-spins feature rules. Omit for a game without free spins. */
  freeSpins?: FreeSpinsConfig

  /**
   * An opt-in line rule for a wild that doubles when it substitutes and
   * pays its own line. Omit for the classic rule (a wild stands in for any
   * regular symbol and a line of wilds pays the wild). It is the game's
   * definition, never a configuration's setting, and a cabinet does not get
   * it (it is not in `PresentationConfig`).
   */
  wildRule?: WildRuleConfig
}

/**
 * The doubling wild's line rule (The Money Game's businessman). The game
 * must have exactly one wild symbol, without the scatter trait. A line is
 * then paid as follows, the engine's `evaluateSinglePayline`:
 * 1. The symbol a wild completes (the first symbol that is no wild, from
 *    the left; never a scatter or bonus) pays its pay for the count of
 *    symbols that match it (wilds included) times `substitutionMultiplier`,
 *    once, whatever the number of wilds; a line without a wild is not doubled.
 * 2. A leading run of wilds pays the wild's own pay for the run's length,
 *    when the wild has one for that count, also when the run ends on a
 *    scatter. A run of the whole line pays the wild's pay, without the
 *    multiplier.
 * 3. The line pays the higher of the two; on a tie, the substituted
 *    symbol's win.
 */
export interface WildRuleConfig {
  /** What a substituting wild multiplies the line's win by: a whole number from 2 to 10. */
  substitutionMultiplier: number
}

/**
 * The wins a player may gamble: a paid spin's win, and a free-spins
 * feature's win at its summary. A trigger spin's win is credited at once
 * and never gambled. Named like `PendingWin.source`.
 */
export const gambleWinSourceSchema = z.enum(['spin', 'feature'])
export type GambleWinSource = z.infer<typeof gambleWinSourceSchema>

/**
 * The gamble's rules, enforced by the game server: it offers a gamble only
 * on a win these rules allow, and cuts a ladder to the picks within the
 * cap. `gambleRulesOf` fills in the defaults.
 */
export interface GambleConfig {
  /** Most picks on one ladder, e.g. 5. `0` turns the gamble off. */
  maxAttempts: number

  /** The wins that may be gambled, each once. Omit for both. */
  wins?: GambleWinSource[]

  /**
   * The most one pick may risk, as a multiple of the round's total bet: a
   * win above it cannot be gambled, and a ladder ends before the pick whose
   * amount at risk (the win, doubled by every won pick) would pass it. Omit
   * for no cap.
   */
  maxStakeMultiplier?: number
}

/**
 * When free spins trigger and how many games they award. The kind, the
 * trigger symbol and the cap are the game's definition; the trigger count,
 * the award and whether a retrigger adds it again are a configuration's
 * settings (`LineSlotSettings['freeSpins']`). The feature's own rules
 * (what a free game pays, what is picked at trigger) belong to its kind,
 * registered in the engine's `featureKinds`.
 */
export interface FreeSpinsConfig {
  /** The server rule set: a key of `featureKinds` (and of `featureDataDecoders`). */
  featureKind: FeatureKind

  /**
   * The symbol whose count of grid cells triggers the feature on a paid
   * spin and retriggers it on a free game, wherever it lands.
   */
  triggerSymbolId: string

  /** Cells of the trigger symbol needed to (re)trigger, e.g. 3. */
  triggerCount: number

  /** Games a trigger awards, e.g. 10. */
  award: number

  /** Games a retrigger adds, e.g. 10. 0 turns retriggers off. */
  retriggerAward: number

  /**
   * Most games one feature can award, retriggers included. A retrigger that
   * would take the total past it awards nothing, so a cap below
   * `award + retriggerAward` turns retriggers off. Omit for no limit.
   */
  maxTotalGames?: number

  /**
   * Required for the `freeSpinsMultiplier` kind, refused for the other: the
   * number every line and scatter win of a free game is multiplied by, a
   * whole number from 2 to 10. A rule of the game (its definition), not a
   * setting; the cabinet gets it in the feature's data, not in the
   * presentation config.
   */
  winMultiplier?: number
}

// -----------------------------------------------------------------------------
// Runtime check of the shape
// -----------------------------------------------------------------------------

const nonEmptyString = z.string().min(1)
const nonNegativeInt = z.int().min(0)
const positiveInt = z.int().min(1)

/**
 * A payout's count as a JSON key: a whole number from 1, written the one
 * way `String(count)` writes it. "05" or "5.0" next to "5" would otherwise
 * become the same count, the last one silently winning.
 */
const payoutCount = z
  .string()
  .regex(
    /^[1-9]\d*$/,
    'must be a count of symbols: a whole number from 1, without leading zeros',
  )
  .transform(Number)

const freeSpinsConfigSchema = z.object({
  featureKind: z.custom<FeatureKind>(
    (value) => typeof value === 'string' && isFeatureKind(value),
    { error: 'Invalid input: expected a registered feature kind' },
  ),
  triggerSymbolId: nonEmptyString,
  triggerCount: positiveInt,
  award: positiveInt,
  retriggerAward: nonNegativeInt,
  maxTotalGames: nonNegativeInt.optional(),
  // the range (2 to 10) and which kind takes it are the engine's check
  winMultiplier: positiveInt.optional(),
})

const wildRuleConfigSchema = z.object({
  // the range (2 to 10) is the engine's check, a rule between fields
  substitutionMultiplier: positiveInt,
})

const symbolDefinitionSchema = z.object({
  id: nonEmptyString,
  traits: z.array(z.enum(SymbolTrait)),
  // JSON keys are strings: "3" is the count 3. A win is whole credits
  // (payout × bet), so both are integers.
  payouts: z.record(payoutCount, nonNegativeInt),
})

const gambleConfigSchema = z.object({
  maxAttempts: nonNegativeInt,
  wins: z.array(gambleWinSourceSchema).optional(),
  maxStakeMultiplier: positiveInt.optional(),
})

/**
 * The SHAPE of a `GameConfig` from outside the process: every required
 * field present and of its type, counts and options as integers, no empty
 * list of symbols, strips, lines or options. Unknown fields are dropped,
 * so a reader still takes a config a newer writer added a field to. It
 * checks no rule between fields (a strip naming an unknown symbol, a line
 * option above the paylines): run the engine's `validateGameConfig` on
 * what it returns. `presentationConfigSchema` is built from it.
 */
export const gameConfigSchema = z.object({
  kind: gameKindSchema,
  reelCount: positiveInt,
  rowCount: positiveInt,
  symbols: z.array(symbolDefinitionSchema).min(1),
  reelStrips: z.array(z.array(nonEmptyString).min(1)).min(1),
  paylines: z.array(z.array(nonNegativeInt).min(1)).min(1),
  betOptions: z.array(positiveInt).min(1),
  payLineOptions: z.array(positiveInt).min(1),
  gamble: gambleConfigSchema,
  freeSpins: freeSpinsConfigSchema.optional(),
  wildRule: wildRuleConfigSchema.optional(),
})

// -----------------------------------------------------------------------------
// Settings: what a configuration version stores
// -----------------------------------------------------------------------------

/** The highest pay of a pay group's count, in multiples of the bet. */
export const MAX_PAY = 100_000

/**
 * The highest bet per line a configuration may offer, in hundredths of a
 * credit: 100 credits.
 */
export const MAX_BET_PER_LINE = 10_000

/**
 * The bet steps every game offers, in hundredths of a credit: 0.10, 0.20,
 * 0.50, 1, 2, 5, 10, 20, 50 and 100 credits. A configuration offers any
 * non-empty subset (the admin panel disables a step); a stored bet that is
 * no step passes the check as long as it is within `MAX_BET_PER_LINE`.
 */
export const BET_STEPS = [
  10, 20, 50, 100, 200, 500, 1000, 2000, 5000, 10_000,
] as const

/** The lowest and the highest weight a symbol may have; weights are relative. */
export const MIN_WEIGHT = 1
export const MAX_WEIGHT = 1000

/** The most free games a trigger (and a retrigger) may award. */
export const MAX_FREE_GAMES_AWARD = 100

/**
 * The most picks one gamble ladder may have: the engine's
 * `MAX_VALUES_PER_DRAW` (a ladder's picks are one draw), the limit its
 * `validateGameConfig` enforces on `gamble.maxAttempts`.
 */
export const MAX_GAMBLE_ATTEMPTS = 8

/**
 * The settings of a `lineSlot` game, what a configuration version stores:
 * the bet and line options, the gamble's rules, the free games' numbers
 * (trigger count, games awarded, whether a retrigger awards them again),
 * the pays of each of the game's pay groups by count, the weight of each
 * symbol and one reel strip per reel (built from the weights by the
 * backend, never edited by hand). Everything else (grid, symbols and their traits, pay groups,
 * lines, the free games' kind, trigger symbol and cap, strip rules) is
 * fixed in the game's definition in code (`@sirius/game-engine`), which
 * the engine combines with these into a `GameConfig` (`gameConfigOf`).
 * Strict: an unknown field anywhere is an issue, so a typo'd key never
 * saves. `freeSpins` is optional here: whether a game needs it (exactly
 * when its definition has free spins) and whether its trigger count is
 * one its trigger symbol pays is the engine's check against the
 * definition. `schemaVersion` is 1; until go-live a format change keeps it
 * and dev databases are reset.
 */
export const lineSlotSettingsSchema = z.strictObject({
  schemaVersion: z.literal(1),
  betOptions: z.array(positiveInt.max(MAX_BET_PER_LINE)).min(1),
  lineOptions: z.array(positiveInt).min(1),
  gamble: z.strictObject({
    maxAttempts: nonNegativeInt,
    wins: z.array(gambleWinSourceSchema).optional(),
    maxStakeMultiplier: positiveInt.optional(),
  }),
  // retriggers: a retrigger adds `award` again (the engine's
  // retriggerAward), or retriggers are off (0)
  freeSpins: z
    .strictObject({
      triggerCount: positiveInt,
      award: positiveInt.max(MAX_FREE_GAMES_AWARD),
      retriggers: z.boolean(),
    })
    .optional(),
  // pay group key → count → pay, e.g. { "explorer": { "5": 5000 } }
  pays: z.record(
    nonEmptyString,
    z.record(payoutCount, positiveInt.max(MAX_PAY)),
  ),
  // symbol id → how often it shows, relative to the others (the engine
  // checks the keys and MIN_WEIGHT..MAX_WEIGHT, so a bad weight is an issue
  // at the weight and the settings still combine)
  weights: z.record(nonEmptyString, z.number()),
  // built from the weights by the backend (`@sirius/game-engine`'s strip
  // generator); never edited by hand
  reels: z.array(z.array(nonEmptyString).min(1)).min(1),
})

/** A `lineSlot` game's settings, as `lineSlotSettingsSchema` parses them. */
export type LineSlotSettings = z.output<typeof lineSlotSettingsSchema>

// -----------------------------------------------------------------------------
// Presentation config: what a cabinet gets
// -----------------------------------------------------------------------------

/** A symbol as a cabinet sees it: what it draws and its paytable row. */
export type PresentationSymbol = Pick<
  SymbolDefinition,
  'id' | 'traits' | 'payouts'
>

/**
 * The part of a `GameConfig` a cabinet presents: the grid, the symbols and
 * their pays, the lines, the bet and line options, the gamble's rules and
 * the free games' trigger symbol, trigger count and awards (rules its
 * paytable shows). No reel strips, no RTP, no feature kind or cap and
 * nothing else the client does not read: a cabinet, its network and its
 * players never see the math they would need to work out the odds. The backend sends
 * `presentationConfig(config)` on `cabinet.boot` and the boot read of
 * `game.state`; the game server plays by the whole `GameConfig`.
 */
export interface PresentationConfig extends Pick<
  GameConfig,
  | 'kind'
  | 'reelCount'
  | 'rowCount'
  | 'paylines'
  | 'betOptions'
  | 'payLineOptions'
  | 'gamble'
> {
  symbols: PresentationSymbol[]
  /** Present exactly when the game has free spins. */
  freeSpins?: Pick<
    FreeSpinsConfig,
    'triggerSymbolId' | 'triggerCount' | 'award' | 'retriggerAward'
  >
}

/** The keys `presentationConfig` keeps, at each level it copies. */
const PRESENTED = {
  config: [
    'kind',
    'reelCount',
    'rowCount',
    'symbols',
    'paylines',
    'betOptions',
    'payLineOptions',
    'gamble',
    'freeSpins',
  ],
  symbol: ['id', 'traits', 'payouts'],
  gamble: ['maxAttempts', 'wins', 'maxStakeMultiplier'],
  freeSpins: ['triggerSymbolId', 'triggerCount', 'award', 'retriggerAward'],
} as const satisfies {
  config: readonly (keyof PresentationConfig)[]
  symbol: readonly (keyof PresentationSymbol)[]
  gamble: readonly (keyof GambleConfig)[]
  freeSpins: readonly (keyof NonNullable<PresentationConfig['freeSpins']>)[]
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

/** A record of only the `keys` `source` has as own keys; any other value as it is. */
function pickKeys(value: unknown, keys: readonly string[]): unknown {
  if (!isRecord(value)) {
    return value
  }
  const picked: Record<string, unknown> = {}
  for (const key of keys) {
    if (Object.hasOwn(value, key)) {
      picked[key] = value[key]
    }
  }
  return picked
}

/**
 * The presentation config of `config`: a copy of only the fields a cabinet
 * presents (`PresentationConfig`), an allowlist at every level, so a field
 * added to `GameConfig` later reaches no cabinet until it is added here.
 * It checks nothing: on unchecked JSON (a stored config) it copies the
 * same keys where they are and leaves any other value as it is, so a
 * broken config still reaches the cabinet, which refuses it
 * (`presentationConfigSchema`) like it refused the whole one. The values
 * it keeps are the input's own (arrays and payouts are not copied).
 */
export function presentationConfig(config: GameConfig): PresentationConfig
export function presentationConfig(
  config: Record<string, unknown>,
): Record<string, unknown>
export function presentationConfig(
  config: GameConfig | Record<string, unknown>,
): PresentationConfig | Record<string, unknown> {
  const presented = pickKeys(config, PRESENTED.config) as Record<
    string,
    unknown
  >
  if (Array.isArray(presented.symbols)) {
    presented.symbols = presented.symbols.map((symbol: unknown) =>
      pickKeys(symbol, PRESENTED.symbol),
    )
  }
  if (Object.hasOwn(presented, 'gamble')) {
    presented.gamble = pickKeys(presented.gamble, PRESENTED.gamble)
  }
  if (Object.hasOwn(presented, 'freeSpins')) {
    presented.freeSpins = pickKeys(presented.freeSpins, PRESENTED.freeSpins)
  }
  return presented
}

/**
 * The SHAPE of a `PresentationConfig` from outside the process (the
 * backend's `cabinet.boot` and boot read): `gameConfigSchema`'s rules for
 * the fields it keeps. Unknown fields are dropped, so a cabinet still boots
 * a config a newer backend presents more of.
 */
export const presentationConfigSchema = gameConfigSchema
  .pick({
    kind: true,
    reelCount: true,
    rowCount: true,
    paylines: true,
    betOptions: true,
    payLineOptions: true,
    gamble: true,
  })
  .extend({
    symbols: z
      .array(
        gameConfigSchema.shape.symbols.element.pick({
          id: true,
          traits: true,
          payouts: true,
        }),
      )
      .min(1),
    freeSpins: gameConfigSchema.shape.freeSpins
      .unwrap()
      .pick({
        triggerSymbolId: true,
        triggerCount: true,
        award: true,
        retriggerAward: true,
      })
      .optional(),
  })

/**
 * One reason a game config or settings are refused: `path` leads from
 * their root to the field (`['reels', 2, 7]`), `message` says what it
 * must be.
 */
export interface GameConfigIssue {
  path: (string | number)[]
  /** the English text; the panel translates `code` and falls back to this */
  message: string
  /** what the issue is, stable across wording changes; see `GameConfigIssueCode` */
  code: GameConfigIssueCode
  /** the numbers and words the translated text names (limits, symbols) */
  params?: Record<string, string | number>
}

/** Same message as admin-web's config editor shows for an unknown field. */
const UNKNOWN_FIELD = 'is not a known field: remove it'

/**
 * Checks the shape of settings a person wrote (`lineSlotSettingsSchema`)
 * and lists every issue at its path, relative to the settings: one per
 * unknown field, at the field, and a bad pay count at its key. On success
 * `settings` is the parsed settings. The engine's `checkGameSettings` runs
 * this, then its checks against the game's definition; the config editor
 * runs it while the settings are typed.
 */
export function checkSettingsShape(
  value: unknown,
):
  | { ok: true; settings: LineSlotSettings }
  | { ok: false; issues: GameConfigIssue[] } {
  const parsed = lineSlotSettingsSchema.safeParse(value)
  if (parsed.success) {
    return { ok: true, settings: parsed.data }
  }
  return {
    ok: false,
    issues: parsed.error.issues.flatMap((issue): GameConfigIssue[] => {
      const path = issue.path.map((key) =>
        typeof key === 'symbol' ? String(key) : key,
      )
      if (issue.code === 'unrecognized_keys') {
        return issue.keys.map((key) => ({
          path: [...path, key],
          message: UNKNOWN_FIELD,
          code: 'UNKNOWN_FIELD',
          params: { field: key },
        }))
      }
      // zod says only "Invalid key in record"; the key's own issue says why
      const reported =
        issue.code === 'invalid_key' ? (issue.issues[0] ?? issue) : issue
      return [{ path, message: reported.message, ...zodCodeOf(reported) }]
    }),
  }
}

const text = (value: unknown) =>
  typeof value === 'string' ? value : JSON.stringify(value)

/** The code and params of one of zod's own issues: `ZOD_<ISSUE_CODE>`. */
function zodCodeOf(
  issue: z.core.$ZodIssue,
): Pick<GameConfigIssue, 'code' | 'params'> {
  switch (issue.code) {
    case 'invalid_type':
      return { code: 'ZOD_INVALID_TYPE', params: { expected: issue.expected } }
    case 'too_small':
      return {
        code: 'ZOD_TOO_SMALL',
        params: {
          minimum: Number(issue.minimum),
          origin: issue.origin,
          inclusive: String(issue.inclusive ?? false),
        },
      }
    case 'too_big':
      return {
        code: 'ZOD_TOO_BIG',
        params: {
          maximum: Number(issue.maximum),
          origin: issue.origin,
          inclusive: String(issue.inclusive ?? false),
        },
      }
    case 'invalid_value':
      return {
        code: 'ZOD_INVALID_VALUE',
        params: { values: issue.values.map(text).join(', ') },
      }
    case 'invalid_format':
      return { code: 'ZOD_INVALID_FORMAT', params: { format: issue.format } }
    case 'not_multiple_of':
      return {
        code: 'ZOD_NOT_MULTIPLE_OF',
        params: { divisor: Number(issue.divisor) },
      }
    case 'invalid_union':
      return { code: 'ZOD_INVALID_UNION' }
    case 'invalid_key':
      return { code: 'ZOD_INVALID_KEY' }
    case 'invalid_element':
      return { code: 'ZOD_INVALID_ELEMENT' }
    case 'unrecognized_keys':
      return { code: 'ZOD_UNRECOGNIZED_KEYS' }
    default:
      return { code: 'ZOD_CUSTOM' }
  }
}

/**
 * A path into a config as JSONPath, the way a ConfigError names it:
 * `['symbols', 0, 'id']` is `$.symbols[0].id`.
 */
export function configPath(path: readonly PropertyKey[]): string {
  return path.reduce<string>(
    (prefix, key) =>
      typeof key === 'number'
        ? `${prefix}[${key}]`
        : `${prefix}.${String(key)}`,
    '$',
  )
}
